/**
 * Help Interaction Tracking
 *
 * Tracks when users seek help through various means:
 * - Visiting the help page
 * - Searching help content
 * - Opening help modals
 * - Starting tutorials
 * - Clicking documentation links
 */

import { api } from "@/lib/api";
import { getSessionId, canTrack } from "./session";
import type { HelpInteractionType } from "@dmarc-platform/shared";

/**
 * Track a help interaction
 *
 * @param interactionType - The type of help interaction
 * @param sourcePage - The page the user was on
 * @param searchQuery - The search query (for help_search type)
 * @param helpTopic - The help topic or section
 */
export function trackHelpInteraction(params: {
  interactionType: HelpInteractionType;
  sourcePage?: string;
  searchQuery?: string;
  helpTopic?: string;
}): void {
  if (!canTrack()) return;

  api
    .trackHelpInteraction({
      sessionId: getSessionId(),
      interactionType: params.interactionType,
      sourcePage: params.sourcePage || window.location.pathname,
      searchQuery: params.searchQuery,
      helpTopic: params.helpTopic,
    })
    .catch(() => {
      // Silently fail - analytics should never break the app
    });
}

/**
 * Track a help page visit
 */
export function trackHelpPageVisit(sourcePage?: string): void {
  trackHelpInteraction({
    interactionType: "help_page_visit",
    sourcePage,
  });
}

/**
 * Track a help search
 */
export function trackHelpSearch(query: string, sourcePage?: string): void {
  trackHelpInteraction({
    interactionType: "help_search",
    searchQuery: query,
    sourcePage,
  });
}

/**
 * Track when a help section is expanded
 */
export function trackHelpSectionExpand(
  section: string,
  sourcePage?: string
): void {
  trackHelpInteraction({
    interactionType: "help_section_expand",
    helpTopic: section,
    sourcePage,
  });
}

/**
 * Track when a help modal is opened
 */
export function trackHelpModalOpen(topic?: string, sourcePage?: string): void {
  trackHelpInteraction({
    interactionType: "help_modal_open",
    helpTopic: topic,
    sourcePage,
  });
}

/**
 * Track when a tutorial is started
 */
export function trackTutorialStart(
  tutorialId: string,
  sourcePage?: string
): void {
  trackHelpInteraction({
    interactionType: "tutorial_start",
    helpTopic: tutorialId,
    sourcePage,
  });
}

/**
 * Track when a tutorial is completed
 */
export function trackTutorialComplete(
  tutorialId: string,
  sourcePage?: string
): void {
  trackHelpInteraction({
    interactionType: "tutorial_complete",
    helpTopic: tutorialId,
    sourcePage,
  });
}

/**
 * Track when a contextual help icon is clicked
 */
export function trackContextualHelpClick(
  topic: string,
  sourcePage?: string
): void {
  trackHelpInteraction({
    interactionType: "contextual_help_click",
    helpTopic: topic,
    sourcePage,
  });
}

/**
 * Track when an external documentation link is clicked
 */
export function trackDocumentationLinkClick(
  linkUrl: string,
  sourcePage?: string
): void {
  trackHelpInteraction({
    interactionType: "documentation_link_click",
    helpTopic: linkUrl,
    sourcePage,
  });
}
